import { cn } from '~/utils/cn';
import { formatCentsToBRL } from '~/utils/formatFinance';
import { relativeDueLabel } from '~/utils/contractLabels';
import type {
  AlertHeroSummary,
  AttentionItem,
} from '~/store/contracts/selectors';

import BankAvatar from '../../atoms/BankAvatar/view';
import { Icon } from '../../atoms/Icon/view';
import { Text } from '../../atoms/Text/view';
import type { propsIconsNames } from '../../atoms/Icon/interfaces';

interface PropsAttentionHero {
  summary: AlertHeroSummary;
  onCta?: () => void;
  className?: string;
}

interface ToneConfig {
  badge: string;
  badgeText: string;
  badgeLabel: string;
  borderAccent: string;
  icon: propsIconsNames;
  iconBg: string;
  iconColor: string;
  ctaText: string;
  ctaBg: string;
  ctaLabel: string;
  amountClass: string;
}

const TONE: Record<'critical' | 'warning' | 'success', ToneConfig> = {
  critical: {
    badge: 'bg-warning-01',
    badgeText: 'color-warning-06',
    badgeLabel: 'PAGAMENTO ATRASADO',
    borderAccent: 'bg-warning-04',
    icon: 'icon-circle-alert',
    iconBg: 'bg-warning-02',
    iconColor: '#BF3333',
    ctaText: 'color-intense-01',
    ctaBg: 'bg-warning-05',
    ctaLabel: 'Ver pendência',
    amountClass: 'color-warning-06',
  },
  warning: {
    badge: 'bg-alert-01',
    badgeText: 'color-alert-06',
    badgeLabel: 'PRÓXIMO VENCIMENTO',
    borderAccent: 'bg-alert-04',
    icon: 'icon-clock',
    iconBg: 'bg-alert-02',
    iconColor: '#99701F',
    ctaText: 'color-intense-01',
    ctaBg: 'bg-primary-01',
    ctaLabel: 'Ver parcela',
    amountClass: 'color-complement-01',
  },
  success: {
    badge: 'bg-success-01',
    badgeText: 'color-success-07',
    badgeLabel: 'TUDO SOB CONTROLE',
    borderAccent: 'bg-success-04',
    icon: 'icon-circle-check',
    iconBg: 'bg-success-02',
    iconColor: '#007831',
    ctaText: 'color-intense-01',
    ctaBg: 'bg-primary-01',
    ctaLabel: 'Ver próxima parcela',
    amountClass: 'color-complement-01',
  },
};

function HeroEmpty({ tone }: { tone: ToneConfig }) {
  return (
    <div className="relative overflow-hidden rounded-medium bg-intense-01 shadow-level1">
      <div className={cn('absolute left-0 top-0 h-full w-[6px]', tone.borderAccent)} />
      <div className="flex items-center gap-small p-medium pl-large">
        <div
          className={cn(
            'w-[48px] h-[48px] rounded-[14px] flex items-center justify-center shrink-0',
            tone.iconBg,
          )}
        >
          <Icon name={tone.icon} size={22} color={tone.iconColor} rounded={false} />
        </div>
        <div className="flex-1 min-w-0">
          <Text className={cn('text-captionLG uppercase tracking-wide', tone.badgeText)}>
            {tone.badgeLabel}
          </Text>
          <Text className="mt-quark text-paragraphLG color-complement-01">
            Nenhuma parcela em aberto nos próximos 30 dias.
          </Text>
        </div>
      </div>
    </div>
  );
}

function HeroFocused({
  tone,
  focus,
  status,
  extraCount,
  upcoming30Amount,
  onCta,
}: {
  tone: ToneConfig;
  focus: AttentionItem;
  status: 'critical' | 'warning' | 'success';
  extraCount: number;
  upcoming30Amount: number;
  onCta?: () => void;
}) {
  const { installment, contract, daysDiff } = focus;
  const dueLine =
    status === 'critical'
      ? daysDiff === -1
        ? 'Vencida ontem'
        : `Vencida há ${Math.abs(daysDiff)} dias`
      : relativeDueLabel(installment.dueDate);

  return (
    <div className="relative overflow-hidden rounded-medium bg-intense-01 shadow-level2">
      <div className={cn('absolute left-0 top-0 h-full w-[6px]', tone.borderAccent)} />
      <div className="p-medium pl-large">
        <div className="flex items-center gap-smaller">
          <span
            className={cn(
              'inline-flex items-center gap-quark px-smaller py-quark rounded-full',
              tone.badge,
            )}
          >
            <span
              className={cn(
                'h-[6px] w-[6px] rounded-full',
                tone.borderAccent,
              )}
            />
            <Text className={cn('text-captionLG uppercase tracking-wide', tone.badgeText)}>
              {tone.badgeLabel}
            </Text>
          </span>
          {extraCount > 0 ? (
            <Text className="text-captionMD color-intense-06">
              +{extraCount} {extraCount === 1 ? 'parcela' : 'parcelas'}
            </Text>
          ) : null}
        </div>

        <div className="mt-smaller flex items-start gap-small">
          <BankAvatar
            institution={contract?.institution}
            size="lg"
            className="shadow-level1"
          />
          <div className="flex-1 min-w-0">
            <Text className={cn('text-display1 leading-none', tone.amountClass)}>
              {formatCentsToBRL(installment.amount)}
            </Text>
            <Text className="mt-smaller text-bodyLG color-complement-01" numberOfLines={1}>
              {contract?.institution.name ?? 'Contrato'}
              {contract?.installmentsTotal
                ? ` · Parcela ${installment.index}/${contract.installmentsTotal}`
                : ` · Parcela ${installment.index}`}
            </Text>
            <Text className="mt-quark text-bodySM color-intense-06">{dueLine}</Text>
          </div>
        </div>

        {status !== 'success' && upcoming30Amount > 0 ? (
          <div className="mt-small rounded-small bg-intense-02 px-small py-smaller">
            <Text className="text-captionMD color-intense-06">
              Previsto nos próximos 30 dias
            </Text>
            <Text className="mt-quark text-paragraphMD color-complement-01">
              {formatCentsToBRL(upcoming30Amount)}
            </Text>
          </div>
        ) : null}

        {onCta ? (
          <button
            type="button"
            onClick={onCta}
            className={cn(
              'mt-small w-full flex items-center justify-center gap-quark rounded-[12px] py-smaller hover:opacity-90',
              tone.ctaBg,
            )}
          >
            <Text className={cn('text-paragraphMD', tone.ctaText)}>{tone.ctaLabel}</Text>
            <Icon name="icon-arrow-right" size={16} color="#FFFFFF" rounded={false} />
          </button>
        ) : null}
      </div>
    </div>
  );
}

export default function AttentionHero({ summary, onCta, className }: PropsAttentionHero) {
  const tone = TONE[summary.status];

  return (
    <div className={cn(className)}>
      {summary.focus ? (
        <HeroFocused
          tone={tone}
          focus={summary.focus}
          status={summary.status}
          extraCount={
            summary.status === 'critical'
              ? Math.max(0, summary.overdueCount - 1)
              : Math.max(0, summary.upcoming30Count - 1)
          }
          upcoming30Amount={summary.upcoming30Amount}
          onCta={onCta}
        />
      ) : (
        <HeroEmpty tone={tone} />
      )}
    </div>
  );
}
